import { useEffect, useId, useState } from 'react';
import { Button, cn, Input, Spinner } from '@/core/ui';
import { BookOpen, MagnifyingGlass } from '@/core/ui/icons';
import { useLessonSearch } from '../../application/queries';
import type { LessonOption } from '../../domain/types';

interface Props {
  lessonId: string;
  lessonTitle: string;
  onPick: (lesson: LessonOption) => void;
  onClear: () => void;
}

/** Busca de aula do catálogo (`GET /v1/flows/lessons?q=`), a partir de 2 letras. */
export function LessonPicker({ lessonId, lessonTitle, onPick, onClear }: Props) {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const inputId = useId();

  useEffect(() => {
    const t = window.setTimeout(() => setTerm(q.trim()), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  const search = useLessonSearch(term);
  const options = term.length >= 2 ? (search.data ?? []) : [];
  const open = q.trim().length >= 2;

  if (lessonId)
    return (
      <div className="flex items-center gap-3 rounded-xl bg-surface px-3 py-2.5 ring-1 ring-line-strong">
        <BookOpen aria-hidden className="size-4 shrink-0 text-brand-600" />
        <span className="min-w-0 flex-1 truncate text-sm text-fg">{lessonTitle || 'Aula escolhida'}</span>
        <Button size="sm" variant="ghost" onClick={onClear}>
          Trocar
        </Button>
      </div>
    );

  const choose = (o: LessonOption) => {
    onPick(o);
    setQ('');
  };

  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className="block text-xs font-medium text-fg-subtle">
        Aula
      </label>
      <div className="relative">
        <MagnifyingGlass
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle"
        />
        <Input
          id={inputId}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          value={q}
          placeholder="Buscar aula pelo título"
          className="pl-9"
          onChange={(e) => {
            setQ(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (!options.length) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((i) => Math.min(i + 1, options.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              choose(options[active]!);
            }
          }}
        />
        {search.isFetching && (
          <Spinner className="absolute top-1/2 right-3 -translate-y-1/2 text-fg-subtle" />
        )}
      </div>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Aulas encontradas"
          className="max-h-60 overflow-y-auto rounded-xl bg-surface py-1 ring-1 ring-line-strong"
        >
          {options.length === 0 ? (
            <li className="px-3 py-2 text-xs text-fg-subtle">
              {search.isFetching || term !== q.trim() ? 'Buscando…' : 'Nenhuma aula com esse título.'}
            </li>
          ) : (
            options.map((o, i) => (
              <li
                key={o.lesson_id}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(o)}
                className={cn('cursor-pointer px-3 py-2 text-sm', i === active && 'bg-surface-2')}
              >
                <span className="block truncate text-fg">{o.title}</span>
                <span className="block truncate text-xs text-fg-subtle">{o.course_title}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
